import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import CardLifecyclePanel from "../components/CardLifecyclePanel";
import CustomerEditPanel from "../components/CustomerEditPanel";
import { CustomerEmailStatus } from "../components/CustomerEmailStatus";
import WalletLinkPanel from "../components/WalletLinkPanel";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Search, Plus, Trash2, Download, QrCode, Printer, CreditCard, Barcode, MessageSquare } from "lucide-react";
import BarcodeImport from "../components/BarcodeImport";
import { useAuth } from "../context/AuthContext";
import { downloadCsv } from "../lib/download";
import LoyaltyCard from "../components/LoyaltyCard";
import AddressMap from "../components/AddressMap";
import CustomersTrashDialog from "../components/CustomersTrashDialog";
import ElasticHistoryButton from "../components/ElasticHistoryButton";
import CustomerConsentsPanel, { downloadFile } from "../components/CustomerConsentsPanel";
import CustomerSendPanel from "../components/CustomerSendPanel";

export default function Customers() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [form, setForm] = useState({ card_number: "", name: "", phone: "", dob: "", email: "", notes: "" });
  const [selected, setSelected] = useState(null);
  const [txs, setTxs] = useState([]);

  const load = async () => {
    const { data } = await api.get("/customers", { params: q ? { q } : {} });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const openDetail = async (c) => {
    setSelected(c);
    const [{ data }, full] = await Promise.all([
      api.get(`/customers/${c.id}/transactions`),
      api.get(`/customers/${c.id}`).catch(() => null),
    ]);
    setTxs(data);
    if (full?.data) setSelected(full.data);
  };

  // Auto-open della scheda cliente quando arriviamo dalla ricerca avanzata via
  // `/clienti?open=<id>`. Consumiamo il parametro dopo il fetch così una
  // ricarica manuale non riapre continuamente il pannello.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId) return;
    (async () => {
      try {
        const { data } = await api.get(`/customers/${openId}`);
        await openDetail(data);
      } catch {
        toast.error("Cliente non trovato");
      } finally {
        setSearchParams({}, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    try {
      await api.post("/customers", form);
      setForm({ card_number: "", name: "", phone: "", dob: "", email: "", notes: "" });
      setCreating(false);
      toast.success("Cliente creato");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Spostare il cliente nel cestino? Potrai ripristinarlo entro 30 giorni.")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Cliente spostato nel cestino (ripristinabile per 30 giorni)");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <span className="pill pill-red">CLIENTI FEDELI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Anagrafica clienti</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {user?.role === "admin" && (
            <Button data-testid="customers-trash-button" variant="outline" className="border-zinc-800 h-11" onClick={() => setTrashOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" /> Cestino / Backup
            </Button>
          )}
          {user?.role === "admin" && (
            <Button data-testid="customers-barcode-import" variant="outline" className="border-zinc-800 h-11" onClick={() => setBarcodeOpen(true)} title="Importa codici a barre delle carte plastiche">
              <Barcode className="h-4 w-4 mr-2" /> Barcode CSV
            </Button>
          )}
          <BarcodeImport open={barcodeOpen} onClose={() => setBarcodeOpen(false)} onDone={() => load?.()} />
          <Button
            data-testid="customers-export-csv"
            variant="outline"
            className="border-zinc-800 h-11"
            onClick={() => downloadCsv("/export/customers.csv", "clienti.csv").catch(() => toast.error("Errore export"))}
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          {user?.role === "admin" && (
            <Button data-testid="customers-consents-register" variant="outline" className="border-zinc-800 h-11" title="Registro consensi privacy di tutti i clienti (PDF + Excel) per verifiche"
              onClick={async () => { try { await downloadFile("/consents/register.pdf", "registro_consensi.pdf"); await downloadFile("/consents/register.xlsx", "registro_consensi.xlsx"); toast.success("Registro consensi scaricato (PDF + Excel)"); } catch { toast.error("Errore export registro"); } }}>
              <Download className="h-4 w-4 mr-2" /> Registro consensi
            </Button>
          )}
          <Button
            data-testid="customer-request-card-button"
            onClick={() => navigate("/rilascio-card")}
            className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11"
            title="Apri il modulo completo Richiedi card (tipo carta, dati, consensi GDPR)"
          >
            <CreditCard className="h-4 w-4 mr-2" /> Richiedi card
          </Button>
          <Dialog open={creating} onOpenChange={setCreating}>
          <DialogTrigger asChild>
            <Button data-testid="customer-new-button" variant="outline" className="border-zinc-800 h-11">
              <Plus className="h-4 w-4 mr-2" /> Inserimento rapido
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800">
            <DialogHeader><DialogTitle>Nuovo cliente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[
                { k: "card_number", l: "Numero carta*" },
                { k: "name", l: "Nome e cognome*" },
                { k: "phone", l: "Telefono*" },
                { k: "dob", l: "Data di nascita (YYYY-MM-DD)" },
                { k: "email", l: "Email" },
                { k: "address", l: "Via e civico" },
                { k: "zip_code", l: "CAP" },
                { k: "city", l: "Città" },
                { k: "province", l: "Provincia (2 lettere)" },
                { k: "notes", l: "Note" },
              ].map((f) => (
                <div key={f.k}>
                  <Label className="mb-1 inline-block text-zinc-300">{f.l}</Label>
                  <Input
                    data-testid={`customer-form-${f.k}`}
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
              ))}
              <Button data-testid="customer-form-submit" onClick={create} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
                Crea cliente
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="card-elev p-4">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            data-testid="customers-search"
            placeholder="Cerca per nome, telefono, numero carta…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-zinc-950 border-zinc-800 h-11"
          />
        </div>
        <div className="overflow-x-auto mobile-cards-wrap">
          <table className="w-full text-sm mobile-cards">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
              <tr className="text-left">
                <th className="py-2 pr-3">Cliente</th>
                <th className="py-2 pr-3">Carta</th>
                <th className="py-2 pr-3">Telefono</th>
                <th className="py-2 pr-3">Punti</th>
                <th className="py-2 pr-3">Speso</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr className="mobile-cards-empty"><td colSpan={6} className="py-8 text-center text-zinc-500">Nessun cliente</td></tr>
              )}
              {rows.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-zinc-900 hover:bg-zinc-900/50 cursor-pointer"
                  onClick={() => openDetail(c)}
                  data-testid={`customer-row-${c.id}`}
                >
                  <td data-label="Cliente" className="py-3 pr-3">
                    <div className="font-medium flex items-center gap-2">{c.name}
                      {c.unread_wa > 0 && <span className="pill bg-emerald-500 text-zinc-950 text-[10px] font-bold inline-flex items-center gap-1" title={`${c.unread_wa} risposte WhatsApp da leggere`} data-testid={`customer-unread-wa-${c.id}`}><MessageSquare className="h-3 w-3" /> {c.unread_wa}</span>}
                    </div>
                    <div className="text-xs text-zinc-500">{c.email || "—"}</div>
                  </td>
                  <td data-label="Carta" className="py-3 pr-3 font-mono text-xs">{c.card_number}</td>
                  <td data-label="Telefono" className="py-3 pr-3">{c.phone}</td>
                  <td data-label="Punti" className="py-3 pr-3"><span className="pill pill-gold font-mono">{c.points}</span></td>
                  <td data-label="Speso" className="py-3 pr-3 text-zinc-400 font-mono">€ {c.total_spent?.toFixed?.(2) ?? "0.00"}</td>
                  <td data-label="" className="py-3 pr-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {user?.role === "admin" && (
                      <button
                        data-testid={`customer-delete-${c.id}`}
                        onClick={() => remove(c.id)}
                        className="p-2 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-[color:var(--bg-surface)] border-l border-zinc-800 w-full sm:max-w-lg overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading">{selected.name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-3">
                <LoyaltyCard customer={selected} branchBgUrl={selected.branch_card_bg_url || null} />
                <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800 flex flex-col sm:flex-row gap-4 items-center">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&bgcolor=ffffff&color=1e3a8a&data=${encodeURIComponent(selected.card_number)}`}
                    alt="QR"
                    className="rounded-lg border border-zinc-800 bg-white"
                    width={160}
                    height={160}
                    data-testid="customer-qr-image"
                  />
                  <div className="text-center sm:text-left">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-center sm:justify-start gap-1">
                      <QrCode className="h-3 w-3" /> QR Carta
                    </div>
                    <div className="font-mono text-2xl font-bold text-amber-300">{selected.card_number}</div>
                    <div className="text-xs text-zinc-500 mt-1">Scansiona al negozio per identificare il cliente</div>
                    <a
                      href={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(selected.card_number)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="pill pill-gold mt-2 inline-flex"
                      data-testid="customer-qr-download"
                    >
                      Stampa QR
                    </a>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><div className="text-[10px] uppercase text-zinc-500 tracking-widest">Carta</div><div className="font-mono">{selected.card_number}</div></div>
                    <div><div className="text-[10px] uppercase text-zinc-500 tracking-widest">Telefono</div><div>{selected.phone}</div></div>
                    <div><div className="text-[10px] uppercase text-zinc-500 tracking-widest">Punti</div><div className="text-amber-300 font-heading text-2xl font-bold">{selected.points}</div></div>
                    <div><div className="text-[10px] uppercase text-zinc-500 tracking-widest">Speso tot.</div><div className="font-mono">€ {selected.total_spent?.toFixed?.(2) ?? "0.00"}</div></div>
                    {selected.email && <div className="col-span-2"><div className="text-[10px] uppercase text-zinc-500 tracking-widest">Email</div><div>{selected.email}</div><CustomerEmailStatus customer={selected} /></div>}
                    {(selected.address || selected.city) && (
                      <div className="col-span-2">
                        <div className="text-[10px] uppercase text-zinc-500 tracking-widest">Indirizzo</div>
                        <div>{[selected.address, selected.zip_code, selected.city, selected.province].filter(Boolean).join(", ")}</div>
                      </div>
                    )}
                  </div>
                </div>
                {(selected.address || selected.city) && <AddressMap customer={selected} height={200} />}
                <ElasticHistoryButton cardNumber={selected.card_number} testid="customer-crm-history" className="w-full" />
                {user?.role === "admin" && <CustomerEditPanel customer={selected} onSaved={async (c) => { setSelected((s) => ({ ...s, ...c })); await load(); }} />}
                <CustomerConsentsPanel customer={selected} />
                <WalletLinkPanel customer={selected} />
                <CustomerSendPanel customer={selected} />
                <CardLifecyclePanel customer={selected} onChanged={async () => { await load(); const { data } = await api.get(`/customers/${selected.id}`).catch(() => ({ data: null })); if (data) setSelected(data); }} />
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Storico movimenti</div>
                  <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
                    {txs.length === 0 && <li className="text-sm text-zinc-500">Nessun movimento</li>}
                    {txs.map((t) => (
                      <li key={t.id} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 flex justify-between items-center">
                        <div>
                          <div className="text-sm font-medium">
                            {t.type === "earn" ? "Accumulo" : t.type === "redeem" ? `Riscatto · ${t.reward_name || ""}` : "Rettifica"}
                          </div>
                          <div className="text-[11px] text-zinc-500">
                            {new Date(t.created_at).toLocaleString("it-IT")} {t.amount_eur ? `· € ${t.amount_eur.toFixed(2)}` : ""}
                            {t.multiplier && t.multiplier !== 1 ? ` · ${t.multiplier}×` : ""}
                          </div>
                        </div>
                        <span className={`font-mono font-bold ${t.points_delta > 0 ? "text-emerald-300" : "text-red-300"}`}>
                          {t.points_delta > 0 ? "+" : ""}{t.points_delta}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      <CustomersTrashDialog open={trashOpen} onOpenChange={setTrashOpen} onRestored={load} />
    </div>
  );
}
